/**
 * <s-photo-carousel> — Horizontal, swipeable photo carousel with prev/next buttons.
 *
 * Required child markup:
 *   [data-carousel-track] — Scrollable list (overflow-x-auto, scroll-snap). Swipe/trackpad works natively.
 *   [data-carousel-prev]  — Button that scrolls back roughly one view width.
 *   [data-carousel-next]  — Button that scrolls forward roughly one view width.
 *
 * Buttons are disabled at the start/end of the track. Respects reduced motion.
 */
class SPhotoCarousel extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const track = this.querySelector('[data-carousel-track]');
    const prev = this.querySelector('[data-carousel-prev]');
    const next = this.querySelector('[data-carousel-next]');
    if (!track || !prev || !next) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const isRtl = getComputedStyle(this).direction === 'rtl';

    const step = (dir) => {
      const amount = Math.max(track.clientWidth * 0.85, 200) * dir * (isRtl ? -1 : 1);
      track.scrollBy({ left: amount, behavior: reduce.matches ? 'auto' : 'smooth' });
    };

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const pos = Math.abs(track.scrollLeft);
      prev.disabled = pos <= 2;
      next.disabled = pos >= max - 2;
      const controls = this.querySelector('[data-carousel-controls]');
      if (controls) controls.hidden = max <= 2;
    };

    prev.addEventListener('click', () => step(-1), { signal });
    next.addEventListener('click', () => step(1), { signal });
    track.addEventListener('scroll', update, { signal, passive: true });
    window.addEventListener('resize', update, { signal, passive: true });
    track.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', update, { signal, once: true });
    });
    update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

if (!customElements.get('s-photo-carousel')) {
  customElements.define('s-photo-carousel', SPhotoCarousel);
}
